/**
 * The two charts in the app.
 *
 * Both follow the same rules: thin marks, 4px rounded data-ends anchored to the
 * baseline, a 2px surface gap between adjacent fills, recessive axes, and a
 * hover/tap layer — an on-screen chart is interactive, so a bar without a
 * tooltip is a bar that withholds its value.
 *
 * Colour is assigned by the job it does. Ageing is an ORDERED severity ramp, so
 * it gets a one-hue ordinal ramp (validated: monotone lightness, visible step
 * gaps, contrast at the end nearest the surface). Sales is a single series, so
 * it takes the accent and needs no legend — the title names it.
 */
import { useState } from "react";

import { formatMoney } from "../derive/reports.js";

/* ────────────────────────────────────────────────────────── ageing bars ── */

export type AgeingDatum = { label: string; value: number };

const AGE_TOKENS = ["var(--age-1)", "var(--age-2)", "var(--age-3)", "var(--age-4)"];

/**
 * A single stacked bar of the four ageing buckets, with the buckets listed
 * beneath as a legend. Stacked rather than four separate bars because the
 * question is "how much of what we are owed is old", which is a composition.
 */
export function AgeingBar({
  data,
  total,
  onSelect,
  selected,
}: {
  data: AgeingDatum[];
  total: number;
  /**
   * Called when a bucket is tapped, so a caller can open it.
   *
   * When present, the SELECTION is owned by the caller and hover no longer
   * doubles as selection — otherwise moving a mouse across the bar would open
   * and close a list underneath it.
   */
  onSelect?: (index: number | null) => void;
  selected?: number | null;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const controlled = onSelect !== undefined;
  const active = controlled ? (selected ?? hovered) : hovered;

  const setActive = (index: number | null) => {
    if (controlled) onSelect(index);
    else setHovered(index);
  };

  if (total <= 0) {
    return (
      <p className="py-3 text-sm text-ink-3">Nothing outstanding — no ageing to show.</p>
    );
  }

  return (
    <div>
      <div
        className="flex h-3.5 w-full gap-0.5 overflow-hidden rounded-full"
        role="img"
        aria-label={data.map((d) => `${d.label}: ${formatMoney(d.value)}`).join(", ")}
      >
        {data.map((d, i) =>
          d.value <= 0 ? null : (
            <button
              key={d.label}
              type="button"
              // Hit target is the full segment; the visual is only 14px tall.
              className="h-full min-w-[3px] cursor-pointer transition-opacity first:rounded-l-full last:rounded-r-full"
              style={{
                width: `${(d.value / total) * 100}%`,
                background: AGE_TOKENS[i],
                opacity: active === null || active === i ? 1 : 0.4,
              }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(i)}
              onBlur={() => setHovered(null)}
              onClick={() => setActive(active === i ? null : i)}
              aria-label={
                controlled
                  ? `${d.label}: ${formatMoney(d.value)} — open this bucket`
                  : `${d.label} days: ${formatMoney(d.value)}`
              }
            />
          ),
        )}
      </div>

      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
        {data.map((d, i) => {
          const row = (
            <>
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                style={{ background: AGE_TOKENS[i] }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-left text-ink-3">{d.label}</span>
              <span className="tabular shrink-0 font-medium text-ink">
                {d.value > 0 ? formatMoney(d.value) : "—"}
              </span>
            </>
          );

          // A 14px-tall segment is a poor tap target, and a bucket holding 2% of
          // the total is nearly impossible to hit. The legend row is the real
          // one on a phone.
          return (
            <li key={d.label} style={{ opacity: active === null || active === i ? 1 : 0.45 }}>
              {controlled && d.value > 0 ? (
                <button
                  type="button"
                  onClick={() => setActive(active === i ? null : i)}
                  aria-expanded={selected === i}
                  className={
                    "flex w-full items-center gap-2 rounded-lg py-1 text-xs transition-colors active:bg-surface-2 " +
                    (selected === i ? "font-semibold" : "")
                  }
                >
                  {row}
                  <svg
                    viewBox="0 0 24 24"
                    className="h-3 w-3 shrink-0 text-ink-3 transition-transform"
                    style={{ transform: selected === i ? "rotate(90deg)" : "none" }}
                    aria-hidden
                  >
                    <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              ) : (
                <span className="flex items-center gap-2 text-xs">{row}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ───────────────────────────────────────────────────────── column chart ── */

export type Column = { label: string; value: number };

/**
 * A small column chart for a single measure over time — sales by month.
 *
 * Only the extremes are labelled directly. A number above every column is
 * noise, and the hover layer covers the rest.
 */
export function MiniColumns({ data, height = 132 }: { data: Column[]; height?: number }) {
  const [active, setActive] = useState<number | null>(null);

  if (data.length === 0) {
    return <p className="py-3 text-sm text-ink-3">No history yet.</p>;
  }

  const max = Math.max(...data.map((d) => d.value), 0);
  const peak = data.reduce((best, d, i) => (d.value > data[best]!.value ? i : best), 0);
  const shown = active ?? peak;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-2xl font-semibold tracking-tight text-ink">
          {formatMoney(data[shown]?.value ?? 0)}
        </span>
        <span className="text-xs text-ink-3">{data[shown]?.label}</span>
      </div>

      <div className="mt-3 flex items-end gap-1.5" style={{ height }}>
        {data.map((d, i) => {
          // A zero-value month still needs a visible tick, or the axis reads as
          // "no data" rather than "no sales".
          const h = max <= 0 ? 2 : Math.max(2, (d.value / max) * (height - 18));
          return (
            <button
              key={d.label}
              type="button"
              className="group flex h-full min-w-0 flex-1 cursor-pointer flex-col justify-end"
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(i)}
              onBlur={() => setActive(null)}
              onClick={() => setActive(active === i ? null : i)}
              aria-label={`${d.label}: ${formatMoney(d.value)}`}
            >
              <span
                className="w-full rounded-t-[4px] transition-colors"
                style={{
                  height: h,
                  background: i === shown ? "var(--accent)" : "var(--surface-3)",
                }}
              />
            </button>
          );
        })}
      </div>

      <div className="mt-1.5 flex gap-1.5">
        {data.map((d, i) => (
          <span
            key={d.label}
            className="min-w-0 flex-1 truncate text-center text-[10px] text-ink-3"
            style={{ fontWeight: i === shown ? 600 : 400 }}
          >
            {d.label.split(" ")[0]}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── meter ──── */

/**
 * Two opposing quantities on one track — receivable against payable. Not a
 * chart so much as a proportion made visible, which is all the comparison
 * needs.
 */
export function OpposingBar({
  left,
  right,
  leftLabel,
  rightLabel,
}: {
  left: number;
  right: number;
  leftLabel: string;
  rightLabel: string;
}) {
  const total = left + right;
  const leftPct = total <= 0 ? 50 : (left / total) * 100;

  return (
    <div>
      <div className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-surface-3">
        <span style={{ width: `${leftPct}%`, background: "var(--accent)" }} />
        <span style={{ width: `${100 - leftPct}%`, background: "var(--accent-2)" }} />
      </div>
      <div className="mt-2 flex justify-between text-xs">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: "var(--accent)" }} aria-hidden />
          <span className="text-ink-3">{leftLabel}</span>
          <span className="tabular font-medium text-ink">{formatMoney(left)}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="tabular font-medium text-ink">{formatMoney(right)}</span>
          <span className="text-ink-3">{rightLabel}</span>
          <span className="h-2 w-2 rounded-full" style={{ background: "var(--accent-2)" }} aria-hidden />
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── ranked bars ── */

export type RankedDatum = { label: string; value: number };

/**
 * Horizontal bars, longest first — the honest form for "which of these is
 * biggest".
 *
 * ONE COLOUR FOR EVERY BAR, deliberately. A ramp keyed to rank would repaint
 * the survivors whenever a filter changed the ordering, and colour must follow
 * the entity rather than its position. Length already carries the magnitude;
 * a second encoding of the same variable adds nothing and costs the reader.
 *
 * Horizontal rather than vertical because these labels are group and account
 * names — vertical columns would need them rotated, and rotated text on a phone
 * is unreadable.
 */
/**
 * The ranking, extracted so it can be tested.
 *
 * There is no DOM in this package's test environment and adding one would mean
 * a new dependency (CLAUDE.md §4), so the arithmetic lives here where a plain
 * test can reach it and the JSX stays a thin shell around it.
 */
export function rankFor(
  data: RankedDatum[],
  limit: number,
): { rows: RankedDatum[]; max: number } {
  const rows = data
    // Filter on the ABSOLUTE value: an expense of -50,000 is a big bar, not a
    // rounding error, and dropping it because it is negative would silently
    // empty the chart on any debit-signed series.
    .filter((d) => Math.abs(d.value) > 0.005)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .slice(0, limit);

  return { rows, max: Math.max(...rows.map((r) => Math.abs(r.value)), 0) };
}

export function RankedBars({
  data,
  limit = 6,
  tone = "var(--accent)",
}: {
  data: RankedDatum[];
  limit?: number;
  /** override only for a series whose meaning is not neutral (e.g. expenses) */
  tone?: string;
}) {
  const [active, setActive] = useState<number | null>(null);

  // Scaled against the largest bar, not the total: this is a comparison of
  // magnitudes, not a composition, and scaling to the total would leave every
  // bar a sliver whenever one entry dominates.
  const { rows, max } = rankFor(data, limit);

  if (rows.length === 0) return <p className="py-4 text-sm text-ink-3">Nothing to show.</p>;

  return (
    <ul className="space-y-2.5">
      {rows.map((row, i) => (
        <li
          key={row.label}
          onPointerEnter={() => setActive(i)}
          onPointerLeave={() => setActive(null)}
          onFocus={() => setActive(i)}
          onBlur={() => setActive(null)}
          tabIndex={0}
          className="block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={`${row.label}: ${formatMoney(row.value)}`}
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 flex-1 truncate text-[13px] text-ink-2">{row.label}</span>
            <span className="tabular shrink-0 text-[13px] font-medium text-ink">
              {formatMoney(row.value)}
            </span>
          </div>
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-surface-3">
            <span
              className="block h-full rounded-full transition-opacity"
              style={{
                width: `${Math.max(1.5, (Math.abs(row.value) / max) * 100)}%`,
                background: tone,
                opacity: active === null || active === i ? 1 : 0.45,
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ──────────────────────────────────────────────────────── ratio against ── */

/**
 * One ratio against the level it is conventionally judged by.
 *
 * The reference line is the whole point: "1.4" means nothing to most readers,
 * while "1.4, and the line it should clear is 1.0" means something immediately.
 * Only ratios with a genuinely conventional benchmark get one — an invented
 * threshold would be worse than none, so `benchmark` is optional and most
 * callers omit it.
 *
 * The status colour never travels alone; the verdict is spelled out in words
 * beside it.
 */
export function RatioBar({
  value,
  display,
  benchmark,
  benchmarkLabel,
  higherIsBetter = true,
  max,
}: {
  value: number | null;
  display: string;
  benchmark?: number;
  benchmarkLabel?: string;
  higherIsBetter?: boolean;
  /** end of the scale; defaults to twice the benchmark, or the value itself */
  max?: number;
}) {
  if (value === null) {
    return <p className="text-sm text-ink-3">{display}</p>;
  }

  const ceiling = max ?? (benchmark ? benchmark * 2 : Math.abs(value) * 1.25 || 1);
  const width = Math.min(100, Math.max(1.5, (Math.abs(value) / ceiling) * 100));

  const meets = benchmark === undefined ? null : higherIsBetter ? value >= benchmark : value <= benchmark;
  const tone = meets === null ? "var(--accent)" : meets ? "var(--good)" : "var(--warn)";

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="tabular text-[15px] font-semibold text-ink">{display}</span>
        {meets !== null && (
          <span className={`text-xs font-medium ${meets ? "text-good" : "text-warn"}`}>
            {meets ? "above" : "below"} {benchmarkLabel ?? benchmark}
          </span>
        )}
      </div>

      <div className="relative mt-1.5 h-2 w-full overflow-hidden rounded-full bg-surface-3">
        <span className="block h-full rounded-full" style={{ width: `${width}%`, background: tone }} />
        {benchmark !== undefined && benchmark <= ceiling && (
          // A 2px rule, not a coloured zone: the reference is a single value and
          // shading a whole region would imply a tolerance nobody defined.
          <span
            aria-hidden
            className="absolute top-0 h-full w-0.5 bg-ink-3"
            style={{ left: `${(benchmark / ceiling) * 100}%`, opacity: 0.7 }}
          />
        )}
      </div>
    </div>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ pareto curve â”€â”€ */

/**
 * Bars for the individual items, a line for the running total.
 *
 * The two share ONE axis, expressed as a percentage of the whole â€” no second
 * scale. A dual-axis Pareto is the classic version and the classic mistake: the
 * crossing point of the two series then depends on where the author chose to
 * put the axes, and readers take a meaning from it that is not there.
 */
export function ParetoCurve({
  points,
  limit = 25,
}: {
  points: Array<{ label: string; value: number; cumulativePercent: number }>;
  limit?: number;
}) {
  const [active, setActive] = useState<number | null>(null);
  const shown = points.slice(0, limit);
  if (shown.length === 0) return <p className="py-4 text-sm text-ink-3">Nothing to show.</p>;

  const total = points.reduce((t, p) => t + p.value, 0) || 1;
  const height = 150;
  const step = 100 / shown.length;
  const maxShare = Math.max(...shown.map((q) => (q.value / total) * 100), 1);

  const line = shown
    .map((p, i) => `${i * step + step / 2},${height - (p.cumulativePercent / 100) * height}`)
    .join(" ");

  const hit = active === null ? null : shown[active];

  return (
    <div>
      <div className="relative">
        <svg
          viewBox={`0 0 100 ${height}`}
          preserveAspectRatio="none"
          className="h-[150px] w-full"
          role="img"
          aria-label={`Pareto curve over ${points.length} items`}
        >
          {/* The 80% line â€” what the whole chart exists to locate. */}
          <line
            x1="0"
            x2="100"
            y1={height - height * 0.8}
            y2={height - height * 0.8}
            stroke="var(--ink-3)"
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity="0.55"
            vectorEffect="non-scaling-stroke"
          />
          {shown.map((p, i) => {
            const barHeight = (((p.value / total) * 100) / maxShare) * height * 0.55;
            return (
              <rect
                key={p.label}
                x={i * step + step * 0.15}
                y={height - barHeight}
                width={step * 0.7}
                height={Math.max(barHeight, 1)}
                fill="var(--accent)"
                opacity={active === null || active === i ? 0.85 : 0.35}
                onPointerEnter={() => setActive(i)}
                onPointerLeave={() => setActive(null)}
              />
            );
          })}
          <polyline
            points={line}
            fill="none"
            stroke="var(--accent-2)"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
          />
        </svg>
        <span className="pointer-events-none absolute right-0 top-0 text-[10px] text-ink-3">80%</span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-3">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm" style={{ background: "var(--accent)" }} aria-hidden />
          each item
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3" style={{ background: "var(--accent-2)" }} aria-hidden />
          running total
        </span>
      </div>

      <p className="mt-2 min-h-[1.25rem] text-xs text-ink-2">
        {hit
          ? `${hit.label} â€” ${formatMoney(hit.value)}, ${hit.cumulativePercent.toFixed(0)}% cumulative`
          : `Showing the top ${shown.length} of ${points.length} items.`}
      </p>
    </div>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ dot plot â”€â”€ */

/**
 * A range per row: lowest, median, highest.
 *
 * A dot plot rather than bars, because the value is the SPREAD and not the
 * size. Bars from zero would make an item selling at â‚¹2,000â€“2,100 look almost
 * identical to one selling at â‚¹0â€“2,100, which is the opposite of the point.
 */
export function RangeDots({
  rows,
  limit = 12,
  onSelect,
  selected,
}: {
  rows: Array<{ label: string; low: number; median: number; high: number; note?: string }>;
  limit?: number;
  /** when present each row becomes a button, so the range can be opened */
  onSelect?: (label: string | null) => void;
  selected?: string | null;
}) {
  const shown = rows.slice(0, limit);
  if (shown.length === 0) return <p className="py-4 text-sm text-ink-3">Nothing to show.</p>;

  const ceiling = Math.max(...shown.map((r) => r.high), 1);

  return (
    <ul className="space-y-3.5">
      {shown.map((row) => {
        const lowPct = (row.low / ceiling) * 100;
        const highPct = (row.high / ceiling) * 100;
        const medPct = (row.median / ceiling) * 100;
        const open = selected === row.label;

        const body = (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink-2">{row.label}</span>
              <span className="tabular shrink-0 text-[12px] text-ink-3">
                {formatMoney(row.low)} â€“ {formatMoney(row.high)}
              </span>
            </div>
            <div className="relative mt-1.5 h-3">
              <span className="absolute top-1/2 h-0.5 w-full -translate-y-1/2 rounded bg-surface-3" />
              <span
                className="absolute top-1/2 h-0.5 -translate-y-1/2 rounded"
                style={{
                  left: `${lowPct}%`,
                  width: `${Math.max(highPct - lowPct, 0.5)}%`,
                  background: "var(--accent-2)",
                }}
              />
              <span
                className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ left: `${lowPct}%`, background: "var(--ink-3)" }}
                aria-hidden
              />
              <span
                className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ left: `${medPct}%`, background: "var(--accent)" }}
                aria-hidden
              />
              <span
                className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ left: `${highPct}%`, background: "var(--ink)" }}
                aria-hidden
              />
            </div>
            {row.note && <p className="mt-1 text-[11px] text-warn">{row.note}</p>}
          </>
        );

        return (
          <li key={row.label}>
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(open ? null : row.label)}
                aria-expanded={open}
                className={
                  "w-full rounded-lg px-1 py-1 text-left transition-colors active:bg-surface-2 " +
                  (open ? "bg-surface-2" : "")
                }
              >
                {body}
              </button>
            ) : (
              body
            )}
          </li>
        );
      })}
      <li className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-[11px] text-ink-3">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: "var(--ink-3)" }} aria-hidden />
          lowest
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: "var(--accent)" }} aria-hidden />
          median
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: "var(--ink)" }} aria-hidden />
          highest
        </span>
      </li>
    </ul>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ heatmap â”€â”€ */

/**
 * A small grid coloured by count â€” for RFM, where the question is where
 * customers CLUSTER rather than what any one cell holds.
 *
 * One hue, light to dark. This is magnitude, so a sequential ramp is the only
 * honest choice; a rainbow would imply categories that do not exist.
 */
export function Heatmap({
  grid,
  xLabel,
  yLabel,
  describe,
  onSelect,
  selected,
}: {
  /** grid[y][x], y ascending upward */
  grid: number[][];
  xLabel: string;
  yLabel: string;
  describe: (x: number, y: number, value: number) => string;
  /** 1-based coordinates, matching what `describe` receives */
  onSelect?: (cell: { x: number; y: number } | null) => void;
  selected?: { x: number; y: number } | null;
}) {
  const [hovered, setHovered] = useState<{ x: number; y: number } | null>(null);
  // Hover explains; a tap opens. Letting hover select would open and close a
  // list underneath as the pointer crossed the grid.
  const active = hovered ?? (selected ? { x: selected.x - 1, y: selected.y - 1 } : null);
  const max = Math.max(...grid.flat(), 1);

  return (
    <div>
      <div className="flex gap-2">
        <span className="flex w-4 shrink-0 items-center justify-center">
          <span
            className="whitespace-nowrap text-[10px] text-ink-3"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            {yLabel}
          </span>
        </span>

        <div className="flex-1">
          {[...grid].reverse().map((row, ri) => {
            const y = grid.length - 1 - ri;
            return (
              <div key={y} className="flex gap-1 pb-1">
                {row.map((value, x) => (
                  <button
                    key={x}
                    type="button"
                    onPointerEnter={() => setHovered({ x, y })}
                    onPointerLeave={() => setHovered(null)}
                    onFocus={() => setHovered({ x, y })}
                    onBlur={() => setHovered(null)}
                    onClick={() => {
                      if (!onSelect || value === 0) return;
                      const already = selected?.x === x + 1 && selected?.y === y + 1;
                      onSelect(already ? null : { x: x + 1, y: y + 1 });
                    }}
                    aria-label={describe(x + 1, y + 1, value)}
                    aria-pressed={selected?.x === x + 1 && selected?.y === y + 1}
                    className="flex aspect-square flex-1 items-center justify-center rounded text-[10px] font-medium outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    style={{
                      // Opacity over ONE hue: a sequential ramp with monotone
                      // lightness by construction, so it cannot accidentally
                      // become categorical.
                      background:
                        value === 0
                          ? "var(--surface-3)"
                          : `color-mix(in oklab, var(--accent) ${15 + (value / max) * 85}%, var(--surface))`,
                      color: value / max > 0.55 ? "var(--accent-ink)" : "var(--ink-2)",
                      outline:
                        selected?.x === x + 1 && selected?.y === y + 1
                          ? "2px solid var(--ink)"
                          : undefined,
                      outlineOffset: "-2px",
                      cursor: onSelect && value > 0 ? "pointer" : "default",
                    }}
                  >
                    {value || ""}
                  </button>
                ))}
              </div>
            );
          })}
          <div className="mt-1 text-center text-[10px] text-ink-3">{xLabel}</div>
        </div>
      </div>

      <p className="mt-2 min-h-[1.25rem] text-xs text-ink-2">
        {active ? describe(active.x + 1, active.y + 1, grid[active.y]![active.x]!) : "Tap a cell."}
      </p>
    </div>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ lorenz curve â”€â”€ */

/** How much of the revenue sits with how few customers. */
export function ConcentrationCurve({
  points,
  caption,
}: {
  points: Array<{ customerPercent: number; revenuePercent: number }>;
  caption: string;
}) {
  const size = 120;
  const path = points
    .map((p) => `${(p.customerPercent / 100) * size},${size - (p.revenuePercent / 100) * size}`)
    .join(" ");

  return (
    <div>
      <svg viewBox={`0 0 ${size} ${size}`} className="h-[150px] w-full" role="img" aria-label={caption}>
        {/* Perfect evenness. The gap between this and the curve IS the message. */}
        <line
          x1="0"
          y1={size}
          x2={size}
          y2="0"
          stroke="var(--ink-3)"
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity="0.5"
          vectorEffect="non-scaling-stroke"
        />
        <polyline
          points={path}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
        />
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-ink-3">
        <span>customers, biggest first</span>
        <span>share of sales</span>
      </div>
      <p className="mt-2 text-xs text-ink-2">{caption}</p>
    </div>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ waterfall â”€â”€ */

/** Income, less expenses, equals profit â€” with the steps drawn to scale. */
export function Waterfall({
  steps,
}: {
  steps: Array<{ label: string; value: number; kind: "start" | "add" | "subtract" | "total" }>;
}) {
  const max = Math.max(...steps.map((s) => Math.abs(s.value)), 1);

  return (
    <ul className="space-y-3">
      {steps.map((step) => {
        const tone =
          step.kind === "subtract"
            ? "var(--accent-2)"
            : step.kind === "total"
              ? step.value >= 0
                ? "var(--good)"
                : "var(--bad)"
              : "var(--accent)";

        return (
          <li key={step.label}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13px] text-ink-2">{step.label}</span>
              <span className="tabular text-[13px] font-medium text-ink">
                {step.kind === "subtract" ? "âˆ’ " : ""}
                {formatMoney(step.value)}
              </span>
            </div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-surface-3">
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${Math.max((Math.abs(step.value) / max) * 100, 1.5)}%`,
                  background: tone,
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

